import { Checkbox, Input, Modal, ModalProps } from 'antd';
import React from 'react';
import { Helmet } from 'react-helmet-async';

import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { RejectionFeedbackGenerator } from '../sinapse/generate_rejection_feedback';
import { RecruitmentProcessCollection } from '../types';
import EmailForm, { EmailFormValues } from './EmailForm';
import Form from './Form';
import { fixUnorderedListInQuill } from './RichEditor/RichEditor';

export interface RejectCandidateFormValues extends EmailFormValues {
  annotation: string;
  sendFeedback: boolean;
}

export interface RejectCandidateModalProps extends ModalProps {
  helmetTitle: string;
  loading?: boolean;
  onFinish?: (values: RejectCandidateFormValues) => void;
  warningParagraph?: React.ReactNode;
  sinapseProps?: {
    recruitmentProcess?: RecruitmentProcessCollection;
  };
}

const RejectCandidateModal = ({
  okButtonProps,
  helmetTitle,
  loading,
  onFinish,
  warningParagraph,
  sinapseProps = {},
  ...props
}: RejectCandidateModalProps) => {
  const [form] = Form.useForm();
  const sendFeedbackValue = Form.useWatch('sendFeedback', form);
  const messageValue = Form.useWatch('message', form);
  const annotation = Form.useWatch('annotation', form);
  const [readOnly, setReadOnly] = React.useState(false);

  const emailCommunicationTemplatesPages = useInfiniteAPIQuery([
    '/email-communication-templates/',
    {
      params: {
        type: 'COMMUNICATION',
        individual: false,
      },
    },
  ]);

  const emailCommunicationTemplates = usePrefetchPages(
    emailCommunicationTemplatesPages,
    (data) => data.results
  );

  const config = useAPIQuery('/configs/tenant-configurations/');
  const user = useAPIQuery('/users/me/');
  return (
    <Modal
      onOk={() => form.submit()}
      okButtonProps={{ loading, ...okButtonProps }}
      {...props}
      width={1000}
    >
      <Helmet>
        <title>{helmetTitle} | Recrutamento e Seleção</title>
      </Helmet>
      <Form<RejectCandidateFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          annotation: '',
          sendFeedback: false,
          subject: '',
          message: '',
          fromName: config.data?.companyFromName || '',
          replyTo: user.data?.email,
          enableReplyTo: false,
          email_communication_template: null,
          attachments: [],
        }}
        onFinish={(variables) =>
          onFinish?.({ ...variables, message: fixUnorderedListInQuill(variables.message) })
        }
      >
        {warningParagraph}
        <Form.Item
          label="Justificativa"
          name="annotation"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>
        <Form.Item name="sendFeedback" valuePropName="checked">
          <Checkbox>Enviar e-mail de feedback para o candidato</Checkbox>
        </Form.Item>
        {sendFeedbackValue && (
          <EmailForm
            templates={emailCommunicationTemplates.map((emailCommunicationTemplate) => ({
              id: emailCommunicationTemplate.id,
              name: emailCommunicationTemplate.name,
              message: emailCommunicationTemplate.message,
              subject: emailCommunicationTemplate.subject,
              fromName: emailCommunicationTemplate.from_name,
              attachments: emailCommunicationTemplate.attachments,
            }))}
            attachments
            customVariables={[
              { value: '[candidato]', label: 'Nome do Candidato' },
              { value: '[empresa]', label: 'Nome da Empresa' },
              { value: '[vaga]', label: 'Nome da Vaga' },
            ]}
            image
            replyTo
            fromName
            readOnly={readOnly}
            richEditorRules={[{ message: 'Campo obrigatório.', required: sendFeedbackValue }]}
            aiItem={
              <RejectionFeedbackGenerator
                enabled={
                  sinapseProps.recruitmentProcess?.id &&
                  config.data?.enableGenerateRejectionFeedback
                }
                sourceId={sinapseProps.recruitmentProcess?.id}
                setResult={(result) =>
                  form.setFieldValue('message', result?.text.replaceAll('\n', '<br>'))
                }
                textValue={messageValue}
                setIsDisabledInput={setReadOnly}
                input={annotation}
                triggerEnabled={!!annotation}
              />
            }
          />
        )}
      </Form>
    </Modal>
  );
};

export default RejectCandidateModal;
